import indexedDBService from './IndexedDBService';
import { addOnlineStatusListener, checkOnlineStatus, shouldUseOfflineMode } from '../utils/offlineHelper';
import awsAuthService from './aws-auth';
import awsApiService from './aws-api';
import { awsUserService } from './aws-user-service';
import { updateUserProfile, getUserProfile } from '../utils/indexedDBService';

/**
 * Service for synchronizing data between IndexedDB and AWS (DynamoDB/API Gateway)
 */
class SyncService {
  private isSyncing: boolean = false;
  private onlineStatusUnsubscribe: (() => void) | null = null;
  private dataChangeUnsubscribe: (() => void) | null = null;

  /**
   * Start the sync service
   */
  public startService(): void {
    // Listen for online status changes
    this.onlineStatusUnsubscribe = addOnlineStatusListener(this.handleOnlineStatusChange);

    // Listen for data changes in IndexedDB
    this.dataChangeUnsubscribe = indexedDBService.addListener(this.handleDataChange);
  }

  /**
   * Stop the sync service
   */
  public stopService(): void {
    // Clean up listeners
    if (this.onlineStatusUnsubscribe) {
      this.onlineStatusUnsubscribe();
      this.onlineStatusUnsubscribe = null;
    }

    if (this.dataChangeUnsubscribe) {
      this.dataChangeUnsubscribe();
      this.dataChangeUnsubscribe = null;
    }
  }

  /**
   * Handle online status changes
   * @param isOnline Whether the device is currently online
   */
  private handleOnlineStatusChange = (isOnline: boolean) => {
    if (isOnline && !shouldUseOfflineMode()) {
      // If we're back online and not in offline mode by preference, sync data
      this.triggerSync();
    }
  };

  /**
   * Handle data changes in local DB
   */
  private handleDataChange = () => {
    // Only trigger sync if we are online and not in offline mode
    if (navigator.onLine && !shouldUseOfflineMode()) {
      // Debounce slightly to avoid rapid-fire syncs if multiple saves happen
      // But for "real-time" feel, immediate is fine as triggerSync handles concurrency
      this.triggerSync();
    }
  };

  /**
   * Trigger a sync operation manually
   */
  public async triggerSync(): Promise<void> {
    try {
      // Skip if already syncing or if we're offline or if user prefers offline mode
      if (this.isSyncing || !navigator.onLine || shouldUseOfflineMode()) {
        return;
      }

      // Make sure IndexedDB is initialized
      if (!await indexedDBService.isInitialized()) {
        await indexedDBService.initDB();
      }

      await this.synchronizeData();
      // Update localStorage with the last sync time for reference
      const timestamp = new Date().toISOString();
      localStorage.setItem('lastSyncTime', timestamp);
      localStorage.setItem('lastSyncStatus', 'success');
    } catch (error) {
      console.error('Error during manual sync:', error);
      localStorage.setItem('lastSyncStatus', 'failed');
      localStorage.setItem('lastSyncError', String(error));
    }
  }

  /**
   * Perform initial sync of reference data (clients, inventory, etc.)
   * This is critical for offline operation
   */
  private async initialSyncReferencesData(): Promise<void> {
    try {
      // Wait for IndexedDB to be initialized
      if (!await indexedDBService.isInitialized()) {
        await indexedDBService.initDB();
      }

      // Sync inventory and clients in parallel
      await Promise.all([
        this.syncInventoryItems(),
        this.syncClients(),
        this.syncBusinessDetails()
      ]);

    } catch (error) {
      console.error('Error during initial reference data sync:', error);
    }
  }

  /**
   * Helper to get tenantId (Workspace ID). If staff, it points to business owner.
   */
  private async getTenantId(uid: string): Promise<string> {
    try {
      const profile = await getUserProfile(uid);
      return profile?.tenantId || uid;
    } catch (e) {
      return uid;
    }
  }


  /**
   * Sync inventory items from Firestore to IndexedDB
   */
  private async syncInventoryItems(): Promise<void> {
    try {
      // Skip if not online
      if (!navigator.onLine) {
        return;
      }

      const currentUser = this.getCurrentUser();
      if (!currentUser?.uid) {
        return;
      }

      const tenantId = await this.getTenantId(currentUser.uid);

      // Get inventory data from AWS API using tenantId
      const allItems = await awsApiService.getInventory(tenantId);

      if (!allItems || allItems.length === 0) {
        return;
      }

      // Normalize inventory data
      const normalizedItems = allItems.map(item => ({
        id: item.id,
        userId: item.userId || tenantId,
        name: item.name || 'Unnamed Item',
        description: item.description || '',
        sellingPrice: item.sellingPrice || item.rate || 0,
        rate: item.rate || item.sellingPrice || 0,
        hsnCode: item.hsnCode || '',
        gstRate: item.gstRate || 0,
        unit: item.unit || 'Nos',
        stockQuantity: item.stockQuantity || item.stock || 0,
        stock: item.stock || item.stockQuantity || 0,
        category: item.category || '',
        syncStatus: 'synced'
      }));

      // Save all inventory items to IndexedDB
      let successCount = 0;

      for (const item of normalizedItems) {
        try {
          await indexedDBService.saveInventoryItem(item);
          successCount++;

          // Also update in local storage cache for fallback
          const cachedItems = localStorage.getItem('akmo_cache_inventory');
          const itemsArray = cachedItems ? JSON.parse(cachedItems) : [];
          const existingIndex = itemsArray.findIndex((i: any) => i.id === item.id);

          if (existingIndex >= 0) {
            itemsArray[existingIndex] = item;
          } else {
            itemsArray.push(item);
          }

          localStorage.setItem('akmo_cache_inventory', JSON.stringify(itemsArray));
        } catch (error) {
          console.error(`Error saving inventory item ${item.id} to IndexedDB:`, error);
        }
      }

    } catch (error) {
      console.error('Error in syncInventoryItems:', error);
    }
  }

  /**
   * Sync clients from Firestore to IndexedDB
   */
  private async syncClients(): Promise<void> {
    try {
      // Skip if not online
      if (!navigator.onLine) {
        return;
      }

      const currentUser = this.getCurrentUser();
      if (!currentUser?.uid) {
        return;
      }

      const tenantId = await this.getTenantId(currentUser.uid);

      // Get client data from AWS API
      const allClients = await awsApiService.getClients(tenantId);

      if (!allClients || allClients.length === 0) {
        return;
      }

      // Normalize client data to ensure consistent fields
      const normalizedClients = allClients.map(client => ({
        id: client.id,
        userId: client.userId || tenantId,
        name: client.name || 'Unnamed Client',
        email: client.email || '',
        phone: client.phone || client.phoneNumber || '',
        address: client.address || '',
        gstin: client.gstin || client.GSTIN || '',
        state: client.state || '',
        syncStatus: 'synced'
      }));

      // Save all clients to IndexedDB
      let successCount = 0;

      for (const client of normalizedClients) {
        try {
          await indexedDBService.saveClient(client);
          successCount++;

          // Also update in local storage cache for fallback
          const cachedClients = localStorage.getItem('akmo_cache_clients');
          const clientsArray = cachedClients ? JSON.parse(cachedClients) : [];
          const existingIndex = clientsArray.findIndex((c: any) => c.id === client.id);

          if (existingIndex >= 0) {
            clientsArray[existingIndex] = client;
          } else {
            clientsArray.push(client);
          }

          localStorage.setItem('akmo_cache_clients', JSON.stringify(clientsArray));
        } catch (error) {
          console.error(`Error saving client ${client.id} to IndexedDB:`, error);
        }
      }

    } catch (error) {
      console.error('Error in syncClients:', error);
    }
  }

  /**
   * Sync business details from Firestore to IndexedDB
   */
  private async syncBusinessDetails(): Promise<void> {
    try {
      // Skip if not online
      if (!navigator.onLine) {
        return;
      }

      const currentUser = this.getCurrentUser();
      if (!currentUser?.uid) {
        return;
      }

      const tenantId = await this.getTenantId(currentUser.uid);

      // Get business details from AWS API
      const userData = await awsApiService.getBusinessDetails(tenantId);

      if (!userData) {
        return;
      }

      // Extract business details from user data
      const businessDetails = {
        id: tenantId,
        userId: tenantId,
        name: userData.businessName || '',
        gstin: userData.gstin || '',
        address: userData.address || '',
        email: userData.email || '',
        phone: userData.phone || '',
        website: userData.website || '',
        state: userData.state || '',
        bankDetails: userData.bankDetails || {},
        syncStatus: 'synced'
      };

      try {
        // Use the saveBusinessDetails method from IndexedDBService
        await indexedDBService.saveBusinessDetails(businessDetails);
      } catch (error) {
        console.error('Error saving business details to IndexedDB:', error);
      }
    } catch (error) {
      console.error('Error syncing business details:', error);
    }
  }

  /**
   * Get the current user from localStorage
   * This is a fallback when Firebase Auth might not be available in offline mode
   */
  private getCurrentUser() {
    try {
      const authData = localStorage.getItem('authUser');
      if (authData) {
        return JSON.parse(authData);
      }
      return null;
    } catch (error) {
      console.error('Error getting current user from localStorage:', error);
      return null;
    }
  }

  /**
   * Synchronize data between IndexedDB and AWS
   */
  private async synchronizeData(): Promise<void> {
    if (this.isSyncing) {
      return Promise.reject(new Error('Sync already in progress'));
    }

    try {
      this.isSyncing = true;
      // Sync invoices
      await this.syncInvoices();

      // Sync reference data (Bidirectional)
      // First push local changes to AWS
      await this.syncInventoryItemsToAws();
      await this.syncClientsToAws();
      await this.syncBusinessDetailsToAws();
      await this.syncUserProfileToAws();

      // Then pull remote changes from AWS
      await this.syncInventoryItems();
      await this.syncClients();
      await this.syncBusinessDetails();

      return Promise.resolve();
    } catch (error) {
      console.error('Error during data synchronization:', error);
      return Promise.reject(error);
    } finally {
      this.isSyncing = false;
    }
  }

  /**
   * Sync local inventory items to AWS
   */
  private async syncInventoryItemsToAws(): Promise<void> {
    try {
      if (!navigator.onLine) return;

      const currentUser = this.getCurrentUser();
      if (!currentUser?.uid) return;

      const tenantId = await this.getTenantId(currentUser.uid);

      // Get all items from IndexedDB
      // We need a method to get all items including pending ones
      // Since getAllItems returns everything, we can filter for pending
      const allItems = await indexedDBService.getInventoryItems(tenantId);
      const pendingItems = allItems.filter(item => item.syncStatus === 'pending');

      if (pendingItems.length === 0) return;

      for (const item of pendingItems) {
        try {
          // Prepare item for AWS (remove local-only fields if necessary)
          const itemToSave = { ...item };

          // Save to AWS
          const result = await awsApiService.saveInventoryItem(itemToSave);

          // Update local status to synced
          await indexedDBService.saveInventoryItem({
            ...item,
            syncStatus: 'synced',
            updatedAt: item.updatedAt // Keep original update time
          });
        } catch (error) {
          console.error(`Failed to sync inventory item ${item.id} to AWS:`, error);
        }
      }
    } catch (error) {
      console.error('Error in syncInventoryItemsToAws:', error);
    }
  }

  /**
   * Sync local clients to AWS
   */
  private async syncClientsToAws(): Promise<void> {
    try {
      if (!navigator.onLine) return;

      const currentUser = this.getCurrentUser();
      if (!currentUser?.uid) return;

      const tenantId = await this.getTenantId(currentUser.uid);

      const allClients = await indexedDBService.getClients(tenantId);
      const pendingClients = allClients.filter(client => client.syncStatus === 'pending');

      if (pendingClients.length === 0) return;

      for (const client of pendingClients) {
        try {
          const clientToSave = { ...client };
          const result = await awsApiService.saveClient(clientToSave);

          await indexedDBService.saveClient({
            ...client,
            syncStatus: 'synced',
            updatedAt: client.updatedAt
          });
        } catch (error) {
          console.error(`Failed to sync client ${client.id} to AWS:`, error);
        }
      }
    } catch (error) {
      console.error('Error in syncClientsToAws:', error);
    }
  }

  /**
   * Sync local business details to AWS
   */
  private async syncBusinessDetailsToAws(): Promise<void> {
    try {
      if (!navigator.onLine) return;

      const currentUser = this.getCurrentUser();
      if (!currentUser?.uid) return;

      const tenantId = await this.getTenantId(currentUser.uid);

      const business = await indexedDBService.getBusinessDetails(tenantId);

      if (business && business.syncStatus === 'pending') {
        try {
          await awsApiService.saveBusinessDetails(business);

          await indexedDBService.saveBusinessDetails({
            ...business,
            syncStatus: 'synced'
          });
        } catch (error) {
          console.error('Failed to sync business details to AWS:', error);
        }
      }
    } catch (error) {
      console.error('Error in syncBusinessDetailsToAws:', error);
    }
  }

  /**
   * Sync local user profile to AWS
   */
  private async syncUserProfileToAws(): Promise<void> {
    try {
      if (!navigator.onLine) return;

      const currentUser = this.getCurrentUser();
      if (!currentUser?.uid) return;

      const profile = await getUserProfile(currentUser.uid);

      if (profile && profile.syncStatus === 'pending') {
        try {
          // Remove local-only fields before sending if necessary, or AWS service handles it
          // syncStatus will be stripped by AWS service usually or ignored
          // We pass the whole profile or just updates?
          // awsUserService.updateUserProfile expects updates. 
          // But here we might want to ensure consistency.

          // Let's copy relevant fields to update
          const { id, uid, role, email, ...updates } = profile;

          await awsUserService.updateUserProfile(currentUser.uid, updates);

          await updateUserProfile(currentUser.uid, {
            syncStatus: 'synced'
          });

          console.log('User Profile background sync successful');
        } catch (error) {
          console.error('Failed to sync user profile to AWS:', error);
        }
      }
    } catch (error) {
      console.error('Error in syncUserProfileToAws:', error);
    }
  }

  /**
   * Synchronize invoices between IndexedDB and AWS
   */
  private async syncInvoices(): Promise<void> {
    try {
      // Get all invoices that need syncing
      const invoices = await indexedDBService.getAllInvoices();

      if (!invoices || invoices.length === 0) {
        return;
      }

      // Filter invoices that need syncing
      const pendingInvoices = invoices.filter(invoice =>
        invoice.syncStatus === 'pending' || invoice.syncStatus === 'error'
      );

      if (pendingInvoices.length === 0) {
        return;
      }

      // Sync each invoice
      for (const invoice of pendingInvoices) {
        try {
          // Save using AWS API Service
          const result = await awsApiService.saveInvoice(invoice);

          // Update the invoice in IndexedDB as synced
          await indexedDBService.saveInvoice({
            ...invoice,
            awsId: result.id || result.invoiceId,
            syncStatus: 'synced',
            syncError: undefined
          });
        } catch (error) {
          console.error(`Failed to sync invoice ${invoice.invoiceNumber}:`, error);

          // Mark the invoice as failed sync
          await indexedDBService.saveInvoice({
            ...invoice,
            syncStatus: 'error',
            syncError: error instanceof Error ? error.message : 'Unknown error'
          });
        }
      }
    } catch (error) {
      console.error('Invoice synchronization failed:', error);
      throw error;
    }
  }

  /**
   * Synchronize all user data - this is a comprehensive sync operation
   * that syncs all data types in a specific order of importance.
   * Returns a promise that resolves when complete.
   */
  public async syncUserData(): Promise<void> {
    try {
      // Skip if already syncing, offline, or in offline mode
      if (this.isSyncing || !navigator.onLine || shouldUseOfflineMode()) {
        return;
      }

      // Set syncing flag
      this.isSyncing = true;

      const currentUser = this.getCurrentUser();
      if (!currentUser?.uid) {
        this.isSyncing = false;
        return;
      }

      try {
        // Sync data in order of importance:

        // 1. Business Profile (most critical)
        await this.syncBusinessDetails();

        const tenantId = await this.getTenantId(currentUser.uid);

        // 2. User Profile (We fetch the tenant's business details to keep staff up-to-date)
        const userData = await awsApiService.getBusinessDetails(tenantId);
        if (userData) {
          // Update the local business details for staff
          await indexedDBService.saveBusinessDetails({ ...userData, id: tenantId, userId: tenantId });
        }

        // 3. Reference data (clients, inventory, etc.)
        await Promise.all([
          this.syncClients(),
          this.syncInventoryItems()
        ]);

        // 4. Transaction data (invoices, expenses, etc.)
        await this.syncInvoices();

        // Update last sync time
        const timestamp = new Date().toISOString();
        localStorage.setItem('lastSyncTime', timestamp);
        localStorage.setItem('lastSyncStatus', 'success');
        localStorage.setItem('cacheInitialized', 'true');

      } catch (error) {
        console.error('Error during comprehensive sync:', error);
        localStorage.setItem('lastSyncStatus', 'failed');
        localStorage.setItem('lastSyncError', String(error));
        throw error;
      } finally {
        this.isSyncing = false;
      }
    } catch (error) {
      console.error('Exception in syncUserData:', error);
      this.isSyncing = false;
      throw error;
    }
  }
}

const syncService = new SyncService();
export default syncService; 